import { useEffect, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";

export type ReactionType = "like" | "heart" | "fire";

export interface ReactionCount {
  like: number;
  heart: number;
  fire: number;
}

export function useReactions(articleId: number | undefined) {
  const [counts, setCounts] = useState<ReactionCount>({ like: 0, heart: 0, fire: 0 });
  const [userReactions, setUserReactions] = useState<Set<ReactionType>>(new Set());
  const [loading, setLoading] = useState(true);

  const getStorageKey = useCallback(
    (type: ReactionType) => `reaction_${articleId}_${type}`,
    [articleId]
  );

  const fetchReactions = useCallback(async () => {
    if (!articleId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error: supaError } = await supabase
        .from("reactions")
        .select("reaction_type")
        .eq("article_id", articleId);

      if (supaError) throw supaError;

      const newCounts: ReactionCount = { like: 0, heart: 0, fire: 0 };
      data?.forEach((r) => {
        if (r.reaction_type in newCounts) {
          newCounts[r.reaction_type as ReactionType]++;
        }
      });
      setCounts(newCounts);

      // Check localStorage for user reactions
      const reacted = new Set<ReactionType>();
      ["like", "heart", "fire"].forEach((type) => {
        if (localStorage.getItem(getStorageKey(type as ReactionType)) === "1") {
          reacted.add(type as ReactionType);
        }
      });
      setUserReactions(reacted);
    } catch {
      // Silently handle error
    } finally {
      setLoading(false);
    }
  }, [articleId, getStorageKey]);

  const toggleReaction = useCallback(
    async (type: ReactionType) => {
      if (!articleId) return;

      const storageKey = getStorageKey(type);
      const hasReacted = localStorage.getItem(storageKey) === "1";

      try {
        if (hasReacted) {
          // Remove reaction
          const { error: supaError } = await supabase
            .from("reactions")
            .delete()
            .eq("article_id", articleId)
            .eq("reaction_type", type);

          if (supaError) throw supaError;
          localStorage.removeItem(storageKey);
          setUserReactions((prev) => {
            const next = new Set(prev);
            next.delete(type);
            return next;
          });
          setCounts((prev) => ({ ...prev, [type]: Math.max(0, prev[type] - 1) }));
        } else {
          // Add reaction
          const { error: supaError } = await supabase.from("reactions").insert({
            article_id: articleId,
            reaction_type: type,
          });

          if (supaError) throw supaError;
          localStorage.setItem(storageKey, "1");
          setUserReactions((prev) => {
            const next = new Set(prev);
            next.add(type);
            return next;
          });
          setCounts((prev) => ({ ...prev, [type]: prev[type] + 1 }));
        }
      } catch {
        // Silently handle error
      }
    },
    [articleId, getStorageKey]
  );

  useEffect(() => {
    fetchReactions();

    if (!articleId) return;

    const channel = supabase
      .channel(`reactions-${articleId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "reactions", filter: `article_id=eq.${articleId}` },
        () => {
          fetchReactions();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [articleId, fetchReactions]);

  return { counts, userReactions, loading, toggleReaction };
}